import { Button, Divider, Input, Spin, message } from "antd";
import call from "./../../assets/images/call.svg";
import edit from "./../../assets/images/edit.svg";
import onBoardBgImg from "./../../assets/images/login-banner.webp";
import closeVisit from "./../../assets/images/close-visit.svg";
import success from "./../../assets/images/success.svg";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "./service";
import useStore from "../../store/useStore";
import { getDecodedToken, setLocalStorage } from "../../utils/localStorage";
import {
  AUTH_TOKEN,
  TERMS_AND_CONDITIONS_LINK,
  privacy_Policy_Link,
} from "../../utils/constants";
import OtpInput from "react-otp-input";
import { teardownMsg91, MSG91_CONFIG, MSG91_SCRIPT_ID } from "../../utils/msg91Cleanup";
import "./OnBoarding.css";

const OnBoarding = () => {
  const navigate = useNavigate();

  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [isContinueDisabled, setIsContinueDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isOtpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpIncorrect, setIsOtpIncorrect] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isOtpLimitExceeded, setIsOtpLimitExceeded] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [isResending, setIsResending] = useState(false);

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (count > 0) {
      const timer = setInterval(() => {
        setCount((prevCount) => prevCount - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [count]);

  // Function to start/reset the countdown timer
  const startTimer = () => {
    setCount(30);
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 6) {
      setIsOtpIncorrect(true);
      return;
    }

    setIsValidating(true);
    setIsOtpIncorrect(false);

    if ((window as any).verifyOtp) {
      (window as any).verifyOtp(
        otp,
        async (data: any) => {
          const { message: otpMessage, tokenAuth } = data;
          console.log("data", data, tokenAuth);
          // Check if the message is "Invalid OTP"
          if (otpMessage === "Invalid OTP") {
            setIsOtpIncorrect(true);
            setIsValidating(false);
            return;
          }

          try {
            // After successful OTP verification, proceed with login
            setDoctorDetails({ ...doctorDetails, mobileNumber: phoneNumber });
            const loginResponse: any = await login({
              phone_number: "91" + phoneNumber,
              access_token: otpMessage,
            });
            if (loginResponse?.data?.data?.token) {
              setLocalStorage(AUTH_TOKEN, loginResponse?.data?.data?.token);
              const decodedToken = getDecodedToken();
              setDoctorDetails({ ...doctorDetails, ...decodedToken });
              navigate("/");
            } else {
              navigate("/personal-details");
            }
          } catch (error) {
            console.error("Error during login:", error);
            setIsOtpIncorrect(true);
          } finally {
            setIsValidating(false);
          }
        },
        (error: any) => {
          console.error("Error verifying OTP:", error);
          setIsOtpIncorrect(true);
          setIsValidating(false);
        }
      );
    } else {
      console.error("verifyOtp method not found!");
      setIsOtpIncorrect(true);
      setIsValidating(false);
    }
  };

  // Auto-verify when OTP is complete (optional - can be removed if manual submit is preferred)
  useEffect(() => {
    if (otp?.length === 6 && !isValidating) {
      // Auto-verify is optional, user can also click the button
      // handleVerifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Load script on mount, teardown on unmount only
  useEffect(() => {
    const existingScript = document.getElementById(MSG91_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement("script");
      script.id = MSG91_SCRIPT_ID;
      script.src =
        "https://control.msg91.com/app/assets/otp-provider/otp-provider.js";
      script.async = true;
      script.onload = () => {
        if ((window as any).initSendOTP) {
          (window as any).initSendOTP(MSG91_CONFIG);
        }
      };
      document.body.appendChild(script);
    } else if ((window as any).initSendOTP) {
      (window as any).initSendOTP(MSG91_CONFIG);
    }

    return () => {
      teardownMsg91();
    };
  }, []);

  // Re-init captcha when switching back to phone input
  useEffect(() => {
    const captchaEl = document.getElementById("captcha-id");
    if (!isOtpSent && captchaEl && (window as any).initSendOTP) {
      captchaEl.innerHTML = "";
      (window as any).initSendOTP(MSG91_CONFIG);
    }
  }, [isOtpSent]);

  const phoneNumberHandler = (e: any) => {
    const input = e.target.value;

    // Allow only numbers
    if (/^\d*$/.test(input)) {
      setPhoneNumber(input);
      setErrorMsg(""); // Clear error message when user types

      // Update isContinueDisabled based on input length
      if (input.length === 10) {
        setIsContinueDisabled(false);
      } else if (input.length > 10) {
        setIsContinueDisabled(true);
      } else {
        setIsContinueDisabled(true);
      }
    }
  };

  const continueBtnHandler = () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      return;
    }

    // Check for Captcha Verification
    if ((window as any).isCaptchaVerified && !(window as any).isCaptchaVerified()) {
      setErrorMsg("Captcha verification is required. Please check the box to proceed.");
      return;
    }

    // Clear any previous error messages
    setErrorMsg("");
    setLoading(true);

    // Use MSG91 sendOtp method
    if ((window as any).sendOtp) {
      (window as any).sendOtp(
        `91${phoneNumber}`,
        (data: any) => {
          setOtpSent(true);
          setCount(30);
          setErrorMsg(""); // Clear error message on success
          setLoading(false);
          resendOTPToastMsg("Verification code has been sent. Please check your SMS.", true);
        },
        (error: any) => {
          console.error("Error sending OTP:", error);
          setLoading(false);
          resendOTPToastMsg("Failed to send OTP. Please try again.", false);
        }
      );
    } else {
      console.error("sendOtp method not found!");
      setLoading(false);
      resendOTPToastMsg("OTP service is unavailable. Please try again.", false);
    }
  };

  const handleOtpChange = (value: any) => {
    setOtp(value);
  };

  const otpResendHandler = () => {
    if (count > 0 || isOtpLimitExceeded || isResending) {
      return;
    }

    // Check if retryOtp method is available
    if (!(window as any).retryOtp) {
      console.error("retryOtp method not found!");
      setErrorMsg("Retry service is unavailable. Please try again later.");
      return;
    }

    setIsResending(true); // Show loader
    setErrorMsg(""); // Clear previous errors

    // Use MSG91 retryOtp method for resending
    (window as any).retryOtp(
      "11", // Retry type code (11 is standard for MSG91)
      (data: any) => {
        setOtpSent(true);
        setIsOtpLimitExceeded(false);
        setErrorMsg(""); // Clear error message
        startTimer(); // Restart the countdown timer
        setIsResending(false); // Hide loader
        resendOTPToastMsg("OTP has been resent. Please check your SMS.", true);
      },
      (error: any) => {
        console.error("Error retrying OTP:", error);
        setIsResending(false); // Hide loader

        // Check if it's a limit exceeded error
        if (error?.message?.includes("limit") || error?.message?.includes("maxed")) {
          setErrorMsg("Your OTP limit has been exceeded. Please try again later");
          setIsOtpLimitExceeded(true);
          resendOTPToastMsg(
            "Your OTP limit has been exceeded. Please try again later",
            false
          );
        } else {
          setErrorMsg("Failed to resend OTP. Please try again.");
          resendOTPToastMsg("Failed to resend OTP. Please try again.", false);
        }
      }
    );
  };

  const resendOTPToastMsg = (textMsg: string, isSucess: boolean = false) => {
    const uniqueKey = `OTP_SENT_${Date.now()}`;
    message.open({
      key: uniqueKey,
      content: (
        <div className="flex items-center justify-center bg-[#171725E5]">
          {isSucess && <img src={success} className="me-3" alt="success" />}
          <div className="text-white mr-2">{textMsg}</div>
          <img
            src={closeVisit}
            alt="closeVisit"
            onClick={() => message.destroy(uniqueKey)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
      duration: 5,
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div>
        <div
          className={`bg-cover bg-center flex justify-end items-start p-8 text-[#fff]`}
          style={{
            backgroundImage: `url(${onBoardBgImg})`,
            // Keep the banner at the proportions production shipped: 260px tall in the
            // 450px column, i.e. 450:260 = 45:26. Expressing it as a ratio instead of a
            // fixed height means a wider column gets a proportionally taller banner
            // rather than a squashed, heavily-cropped one (the old fixed 260px became
            // ~3.8:1 on a wide screen, so bg-cover ate most of the image).
            // Deliberately NOT the photo's own 3:2 — that made the banner far too tall.
            aspectRatio: "45 / 26",
            borderRadius: "0 0 24px 24px",
            flexDirection: "column",
            rowGap: "12px",
          }}
        >
          {isOtpSent ? (
            <>
              <div className="text-2xl font-bold xs:font-extrabold xs:text-4xl">
                OTP Verification
              </div>
              <div className="text-xs xs:text-sm flex direction-row gap-x-2">
                <span>OTP sent to </span>
                <span style={{ textDecoration: "underline" }}>
                  +91-{phoneNumber}
                </span>
                <img src={edit} alt="edit" onClick={() => setOtpSent(false)} />
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold xs:font-extrabold xs:text-4xl">
                Welcome
              </div>
              <div className="text-2xl font-bold xs:font-extrabold xs:text-4xl">
                Doctor!
              </div>
            </>
          )}
        </div>
      </div>
      {isOtpSent ? (
        <div className="flex flex-col items-start p-6 gap-y-3">
          <label className="text-sm pb-1">Enter OTP</label>
          <OtpInput
            value={otp}
            onChange={handleOtpChange}
            numInputs={6}
            shouldAutoFocus={true}
            containerStyle={`w-full flex !justify-between`}
            inputStyle={`!h-[48px] !w-[48px] border border-[#DCE4E8] rounded-[8px] bg-[#FFFFFF] outline-none ${
              isOtpIncorrect
                ? "border-red-500"
                : "focus:border-primary hover:border-primary"
            }`}
            renderInput={(props) => (
              <input
                {...props}
                disabled={isValidating}
                type="number"
                inputMode="numeric"
              />
            )}
          />
          {isOtpIncorrect && (
            <span className="text-red-400 text-xs">Invalid OTP. Try Again</span>
          )}
          <div className="w-full mt-4">
            <Button
              type="primary"
              className="w-full h-14 rounded-2xl bg-[#4B4AD5] p-5 text-lg font-medium"
              onClick={handleVerifyOtp}
              disabled={otp.length < 6 || isValidating}
              loading={isValidating}
            >
              Verify OTP
            </Button>
          </div>
          {count > 0 ? (
            <div className="text-xs text-left leading-[18px] text-[#5A6774] mt-2">
              Didn't receive OTP? Resend code in {count} sec
            </div>
          ) : isOtpLimitExceeded ? (
            <div>
              <div className="text-xs text-left leading-[18px] text-[#5A6774] flex gap-x-2 mt-2">
                <div className="text-red-400">
                  Your OTP limit has been exceeded. Please try again later
                </div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-left leading-[18px] text-[#5A6774] flex gap-x-2 mt-2">
              <div>Didn't receive OTP?</div>
              <u
                className={`text-[#4B4AD5] font-semibold ${
                  isResending ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
                onClick={otpResendHandler}
              >
                {isResending ? "Resending..." : "Resend OTP"}
              </u>
            </div>
          )}
          {errorMsg && !isOtpLimitExceeded && (
            <div className="text-xs text-left leading-[18px] text-[#5A6774] mt-2">
              <div className="text-red-400">{errorMsg}</div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col items-start p-6 pb-0 gap-y-2">
            <label className="text-sm pb-1">Enter your mobile number</label>
            <Input
              placeholder="Enter your phone number"
              prefix={
                <>
                  <img src={call} alt="user" className="p-2" />
                  {" +91 "}
                </>
              }
              onChange={phoneNumberHandler}
              value={phoneNumber}
              name="doctorName"
              inputMode="numeric"
              maxLength={10}
              autoFocus={true}
            />
            <div id="captcha-id"></div>
            {errorMsg && (
              <div className="text-xs text-left leading-[18px] text-[#5A6774] mt-2">
                <div className="text-red-400">
                  {errorMsg}
                </div>
              </div>
            )}
            <p className="text-xs text-left leading-[18px] text-[#5A6774]">
              By signing up, you agree to Tatvacare{" "}
              <u
                className="font-semibold"
                onClick={() => {
                  window.location.href = TERMS_AND_CONDITIONS_LINK;
                }}
              >
                Terms & Conditions
              </u>{" "}
              and{" "}
              <u
                className="font-semibold"
                onClick={() => {
                  window.location.href = privacy_Policy_Link;
                }}
              >
                Privacy Policy
              </u>
            </p>
          </div>
          <div className="flex flex-col h-full">
            <div className="w-full bottom-0 left-0 h-gt-520:fixed">
              <div className="mx-auto max-w-[1280px] w-full">
                <Divider />
                <div className="p-6 pt-0">
                  <Button
                    type="primary"
                    className="w-full h-14 rounded-2xl bg-[#4B4AD5] p-5 text-lg font-medium"
                    onClick={continueBtnHandler}
                    disabled={isContinueDisabled}
                    loading={loading}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {isValidating && <Spin />}
    </div>
  );
};

export default OnBoarding;